import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Plush Residence';
const SITE_URL = 'https://plushresidence.ca';
const DEFAULT_DESCRIPTION = 'Premium luxury short-term rentals across Ontario. Experience world-class accommodations in Toronto, Mississauga, Niagara Falls & Ottawa. Book your dream stay today.';
const DEFAULT_KEYWORDS = 'luxury rentals Ontario, premium short-term rentals Toronto, luxury accommodations Mississauga, Niagara Falls vacation rentals, Ottawa luxury stays, furnished rentals Toronto, corporate housing Ontario, executive rentals Canada, premium Airbnb alternative Toronto, luxury vacation homes Ontario, short-term furnished apartments, high-end rental properties, plush residence, PlushResidence.ca';
const OG_IMAGE = '/og-image.png';

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  path = '',
  image = OG_IMAGE,
  type = 'website',
  noIndex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Premium Luxury Rentals in Ontario`;
  const canonicalUrl = `${SITE_URL}${path}`;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1536" />
      <meta property="og:image:height" content="1024" />
      <meta property="og:image:alt" content={`${SITE_NAME} — Premium Luxury Rentals`} />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} — Premium Luxury Rentals`} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export const businessJsonLd = {
  "@context": "https://schema.org",
  "@type": "VacationRental",
  "name": "Plush Residence",
  "alternateName": "PlushResidence.ca",
  "url": "https://plushresidence.ca",
  "logo": "https://plushresidence.ca/favicon-512.png",
  "image": "https://plushresidence.ca/og-image.png",
  "description": "Premium luxury short-term rentals across Ontario. Experience world-class accommodations in Toronto, Mississauga, Niagara Falls & Ottawa.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Toronto",
    "addressRegion": "Ontario",
    "addressCountry": "CA"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "43.6532",
    "longitude": "-79.3832"
  },
  "areaServed": [
    { "@type": "City", "name": "Toronto" },
    { "@type": "City", "name": "Mississauga" },
    { "@type": "City", "name": "Niagara Falls" },
    { "@type": "City", "name": "Ottawa" }
  ],
  "priceRange": "$$$$",
  "sameAs": [],
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    "opens": "00:00",
    "closes": "23:59"
  }
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Plush Residence",
  "url": "https://plushresidence.ca",
  "description": "Premium luxury short-term rentals across Ontario",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://plushresidence.ca/locations?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};
